import {
  Avatar,
  Box,
  Chip,
  Grid,
  Modal,
  Stack,
  Tab,
  Tabs,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { Close } from "../../utilities/icons";
import EmployeeDocuments from "../Documents/EmployeeDocuments";
import { employeeFullProfileApi } from "../../apiCalls/Apicalls";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "calc(100vw - 32px)", sm: 680 },
  maxHeight: "calc(100vh - 40px)",
  bgcolor: "var(--ax-surface)",
  borderRadius: "var(--ax-radius-lg)",
  border: "1px solid var(--ax-border)",
  boxShadow: "var(--ax-shadow-lg)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const BLUE = "#418BFF";
const BLUE_OVERLAY = "rgba(65, 139, 255, 0.12)";

function getInitials(row) {
  return `${row?.FirstName?.[0] || ""}${row?.LastName?.[0] || ""}`.toUpperCase();
}

function Field({ label, value }) {
  return (
    <Stack spacing={0.5}>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 600,
          color: "var(--ax-ink-2)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "0.9rem",
          color: "var(--ax-ink)",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Stack>
  );
}

function ProfileTab({ employee }) {
  return (
    <Box sx={{ p: 3, bgcolor: "var(--ax-bg)", overflowY: "auto", flex: 1 }}>
      <Box
        sx={{
          bgcolor: "var(--ax-surface)",
          border: "1px solid var(--ax-border)",
          borderRadius: "var(--ax-radius-lg)",
          p: 3,
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "0.78rem", color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}
        >
          Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Field label="Employee ID" value={employee?.EmpId} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Contact" value={employee?.Contact} />
          </Grid>
          <Grid item xs={12}>
            <Field label="Email" value={employee?.Email} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Job Title" value={employee?.JobTitle} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Employee Type" value={employee?.EmployeeType} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Department" value={employee?.SpaceName} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Department ID" value={employee?.departmentId} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Team" value={employee?.team} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Hierarchy Level" value={employee?.hierarchyLevel} />
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          bgcolor: "var(--ax-surface)",
          border: "1px solid var(--ax-border)",
          borderRadius: "var(--ax-radius-lg)",
          p: 3,
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "0.78rem", color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}
        >
          Reporting Structure
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Field label="Reports To" value={employee?.reportingToName} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Manager" value={employee?.managerName} />
          </Grid>
          <Grid item xs={6}>
            <Field label="Team Lead" value={employee?.teamLeadName} />
          </Grid>
          <Grid item xs={6}>
            <Field label="HR" value={employee?.hrName} />
          </Grid>
        </Grid>
      </Box>

      {employee?.projects?.length > 0 && (
        <Box
          sx={{
            bgcolor: "var(--ax-surface)",
            border: "1px solid var(--ax-border)",
            borderRadius: "var(--ax-radius-lg)",
            p: 3,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "0.78rem", color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em", mb: 2 }}
          >
            Projects ({employee.projects.length})
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {employee.projects.map((p, i) => (
              <Chip
                key={i}
                label={p.name || p.code}
                size="small"
                sx={{
                  bgcolor: BLUE_OVERLAY,
                  color: BLUE,
                  fontWeight: 500,
                  fontSize: "0.74rem",
                  borderRadius: "6px",
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {employee?.totalDocuments > 0 && (
        <Box mt={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
            {Object.entries(employee.documentCounts || {}).map(([cat, count]) => (
              <Chip
                key={cat}
                label={`${cat}: ${count}`}
                size="small"
                sx={{ bgcolor: "var(--ax-bg-secondary)", color: "var(--ax-ink-2)", fontSize: "0.72rem" }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function ViewUser({ open, handleClose, currentRow }) {
  const [tab, setTab] = useState(0);
  const [fullProfile, setFullProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const displayRow = fullProfile || currentRow;
  const fullName =
    `${displayRow?.FirstName || ""} ${displayRow?.LastName || ""}`.trim() || "Employee";

  useEffect(() => {
    if (!open || !currentRow?.EmpId) {
      setTab(0);
      setFullProfile(null);
      return;
    }
    setLoadingProfile(true);
    axios
      .get(employeeFullProfileApi(currentRow.EmpId), {
        headers: { Authorization: Cookies.get("jwtToken") },
      })
      .then((res) => setFullProfile(res.data))
      .catch(() => setFullProfile(null))
      .finally(() => setLoadingProfile(false));
  }, [open, currentRow?.EmpId]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2.25}
          borderBottom="1px solid var(--ax-border)"
          flexShrink={0}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={displayRow?.profileImageUrl || undefined}
              sx={{
                width: 40,
                height: 40,
                bgcolor: BLUE_OVERLAY,
                color: BLUE,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: "10px",
              }}
            >
              {getInitials(displayRow)}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontFamily: "var(--ax-font-heading)",
                  fontWeight: 500,
                  fontSize: "1.05rem",
                  letterSpacing: "-0.015em",
                  color: "var(--ax-ink)",
                }}
              >
                {fullName}
                {loadingProfile && (
                  <CircularProgress size={12} sx={{ ml: 1, color: "var(--ax-blue)" }} />
                )}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "var(--ax-ink-2)" }}>
                {displayRow?.JobTitle || "Employee"} · {displayRow?.SpaceName || "—"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={displayRow?.hierarchyLevel || "Employee"}
              size="small"
              sx={{ bgcolor: BLUE_OVERLAY, color: BLUE, fontWeight: 500, fontSize: "0.72rem", borderRadius: "6px" }}
            />
            <IconButton onClick={handleClose} size="small" sx={{ color: "var(--ax-ink-2)" }}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 3,
            borderBottom: "1px solid var(--ax-border)",
            minHeight: 40,
            flexShrink: 0,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.82rem",
              color: "var(--ax-ink-2)",
              minHeight: 40,
              py: 0,
            },
            "& .Mui-selected": { color: "var(--ax-blue) !important", fontWeight: 600 },
            "& .MuiTabs-indicator": { bgcolor: "var(--ax-blue)" },
          }}
        >
          <Tab label="Profile" />
          <Tab label="Documents" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {tab === 0 && <ProfileTab employee={displayRow} />}
          {tab === 1 && currentRow?.EmpId && (
            <Box p={3} bgcolor="var(--ax-bg)">
              <EmployeeDocuments empId={currentRow.EmpId} />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default ViewUser;
